import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Link2, RefreshCw, ListChecks, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { moveWithUndo } from "@/lib/moveStage";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { BulkUpload } from "@/pages/job/BulkUpload";
import { BatchTracker } from "@/pages/job/BatchTracker";
import { RankedToolbar, ALL_COLS, DEFAULT_COLS } from "@/pages/job/RankedToolbar";
import { RankedTable } from "@/pages/job/RankedTable";
import { SendAssessmentDialog } from "@/pages/assessments/SendAssessmentDialog";
import { Users } from "lucide-react";
import { CopyLinkButton, careersUrl } from "@/components/app/CopyLinkButton";

const SORTS = { rank: (a, b) => (a.rank || 1e9) - (b.rank || 1e9), name: (a, b) => a.name.localeCompare(b.name),
  last_activity: (a, b) => new Date(b.last_activity_at) - new Date(a.last_activity_at) };

function useRanked(jobId) {
  return useQuery({ queryKey: ["ranked", jobId], queryFn: () => get(`/jobs/${jobId}/ranked`),
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : false) });
}

export function CandidatesTab({ job }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = STAFF.includes(user.role);
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [view, setView] = useState({ category: "", stage: "", tag: "", sort: "rank", cols: DEFAULT_COLS });
  const [compact, setCompact] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [topN, setTopN] = useState(10);
  const [rescoring, setRescoring] = useState(null);
  const [sending, setSending] = useState(false);
  const { data: batches } = useQuery({ queryKey: ["batches", job.id], queryFn: () => get(`/jobs/${job.id}/batches`) });
  const { data, isLoading } = useRanked(job.id);
  useEffect(() => { if (!batchId && batches?.length) setBatchId(batches[0].id); }, [batches, batchId]);
  useEffect(() => { if (rescoring && data && !data.pending) { toast.success("Re-scoring finished"); setRescoring(null); } }, [data, rescoring]);

  const rows = useMemo(() => (data?.rows || [])
    .filter((r) => (!view.category || r.category === view.category) && (!view.stage || r.stage === view.stage) && (!view.tag || r.tags.includes(view.tag)))
    .sort(SORTS[view.sort] || SORTS.rank), [data, view]);
  const tags = useMemo(() => [...new Set((data?.rows || []).flatMap((r) => r.tags))].sort(), [data]);
  const labels = Object.fromEntries(ALL_COLS);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ranked", job.id] }); setSelected(new Set()); };
  const shortlist = (ids) => ids.length ? moveWithUndo(ids, "screen_shortlist", refresh) : toast("No candidates to shortlist");
  const eligible = (data?.rows || []).filter((r) => ["onboard", "screen_shortlist"].includes(r.stage) && !r.shortlisted && r.rank);

  const rescore = async () => {
    try { const { data: d } = await api.post(`/jobs/${job.id}/rescore`); setRescoring(d.queued); toast(`Re-scoring ${d.queued} candidates in the background`); qc.invalidateQueries({ queryKey: ["ranked", job.id] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="job-candidates-tab">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setUploading(true)} disabled={uploading} data-tour="pt-add-resumes-btn" data-testid="add-resumes-btn"><Upload size={16} /> Add resumes</Button>
          {job.status === "open" && <CopyLinkButton url={careersUrl(job)} testId="copy-careers-link-btn" />}
          <Button variant="outline" onClick={rescore} disabled={!!data?.pending} data-testid="rescore-all-btn"><RefreshCw size={16} /> Re-score all</Button>
        </div>
      )}
      {!!data?.pending && (
        <div className="panel flex items-center gap-4 px-5 py-3" data-testid="screening-progress">
          <Loader2 size={16} className="animate-spin text-brand" />
          <p className="text-sm text-ink-secondary">{data.pending} candidate(s) being read and scored…</p>
          <Progress value={100 * (1 - data.pending / Math.max(1, data.rows.length))} className="h-2 flex-1 bg-brand-tint" />
        </div>
      )}
      {uploading && <BulkUpload jobId={job.id} onStarted={setBatchId} onClose={() => setUploading(false)} />}
      {batchId && <BatchTracker batchId={batchId} canEdit={canEdit} />}
      {isLoading ? <SkeletonRows rows={8} /> : !data.rows.length ? (
        <EmptyState testId="ranked-empty" icon={Users} title="No candidates yet" text="Add resumes or share the careers page. Each resume is read and ranked automatically." />
      ) : (
        <>
          <RankedToolbar jobId={job.id} view={view} setView={setView} tags={tags} compact={compact} setCompact={setCompact} />
          <div className="flex flex-wrap items-center gap-2">
            <select value={view.sort} onChange={(e) => setView({ ...view, sort: e.target.value })} className="h-9 rounded-lg border border-line bg-white px-2 text-sm" data-tour="pt-ranked-sort" data-testid="ranked-sort">
              <option value="rank">Sort by rank</option><option value="name">Sort by name</option><option value="last_activity">Sort by last activity</option>
            </select>
            <span className="text-sm text-ink-muted" data-testid="ranked-count">{rows.length} shown · {selected.size} selected</span>
            {canEdit && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {selected.size > 0 && <Button size="sm" onClick={() => shortlist([...selected])} data-testid="shortlist-selected-btn"><ListChecks size={14} /> Shortlist selected</Button>}
                {selected.size > 0 && <Button size="sm" variant="outline" onClick={() => setSending(true)} data-testid="send-assessment-selected-btn"><Send size={14} /> Send assessment</Button>}
                <Input type="number" min={1} value={topN} onChange={(e) => setTopN(Number(e.target.value) || 1)} className="h-8 w-16" data-testid="top-n-input" />
                <Button size="sm" variant="outline" onClick={() => shortlist(eligible.sort(SORTS.rank).slice(0, topN).map((r) => r.application_id))} data-tour="pt-shortlist-top-n-btn" data-testid="shortlist-top-n-btn">Shortlist top {topN}</Button>
                <Button size="sm" variant="outline" onClick={() => shortlist(eligible.filter((r) => r.category === "Strong Fit").map((r) => r.application_id))} data-testid="shortlist-strong-btn">Shortlist all Strong Fit</Button>
              </div>
            )}
          </div>
          <RankedTable rows={rows} cols={ALL_COLS.map(([k]) => k).filter((k) => view.cols.includes(k))} labels={labels} selected={selected} setSelected={setSelected} compact={compact} />
          <SendAssessmentDialog jobId={job.id} applicationIds={[...selected]} open={sending} onClose={() => setSending(false)} onSent={refresh} />
        </>
      )}
    </div>
  );
}
